import * as graphics from "@client/app/graphics";
import {ContentModal} from "@client/components/Modal";
import {Scene} from "@client/components/Scene";
import {Shell} from "@client/components/Shell";
import * as constants from "@client/constants";
import * as dates from "@client/dates";
import * as dialogs from "@client/features/dialogs";
import {general} from "@client/features/planner";
import * as forms from "@client/forms";
import * as widgets from "@client/forms/widgets";
import * as onboarding from "@client/journeys/onboarding";
import {RegistrationJourney} from "@client/journeys/registration";
import * as analytics from "@client/shared/analytics";
import {Static} from "@client/shared/components/Static";
import * as typography from "@client/shared/typography";
import {
    ImperialCaloriesContext,
    useSiteContext,
    useStaticURL,
} from "@client/site/context";
import {appStyles} from "@client/style";
import * as utils from "@client/utils";
import {reverse, server} from "@reactivated";
import Decimal from "decimal.js";
import {produce} from "immer";
import React from "react";

type Route =
    | "intro"
    | "profile"
    | "goal"
    | "activity"
    | "nutrition"
    | "settings"
    | "maxes"
    | "results"
    | "program"
    | "review";

type Action =
    | {route: Route; onClick?: never; title?: React.ReactNode; disabled?: boolean}
    | {
          route?: never;
          onClick: () => unknown;
          title?: React.ReactNode;
          disabled?: boolean;
      };

type Nutrition = "STANDARD" | "PROTEIN" | "CUSTOM";

function getStep(
    router: {route: Route; push: (route: Route) => void},
    nutritionForm: ReturnType<typeof useNutritionForm>,
    workouts: server.journal.templates.Start["workouts"],
    handler: ReturnType<typeof onboarding.useHooks>,
    isEmbedded?: boolean,
): {content: React.ReactNode; back: Action | null; secondary?: Action; next: Action} {
    const smallestPlate = new Decimal(
        handler.liveProfile.is_imperial ? "2.5" : "1",
    ).times(2);

    const registrationDialog = dialogs.useDialog<server.core.rpc.Profile.output>({
        Content: RegistrationJourney,
        modal: ContentModal,
    });

    const getHumanWeight = (input: Decimal) => {
        return utils.roundNumberToNearest(input, smallestPlate);
    };

    const exerciseTypeSchema = workouts.exercise_types
        .map((exerciseType) => {
            if (exerciseType.uuid == constants.EXERCISE_TYPES.DEADLIFT) {
                return {
                    exerciseType,
                    topSetRepsTarget: 5,
                    numberOfSets: 2,
                    backoffPercentage: 0,
                };
            }

            if (exerciseType.uuid == constants.EXERCISE_TYPES.SQUAT) {
                return {
                    exerciseType,
                    topSetRepsTarget: 5,
                    numberOfSets: 3,
                    backoffPercentage: 0,
                };
            }

            if (exerciseType.uuid == constants.EXERCISE_TYPES.BENCH) {
                return {
                    exerciseType,
                    topSetRepsTarget: 5,
                    numberOfSets: 3,
                    backoffPercentage: 0,
                };
            }

            if (exerciseType.uuid == constants.EXERCISE_TYPES.PRESS) {
                return {
                    exerciseType,
                    topSetRepsTarget: 5,
                    numberOfSets: 3,
                    backoffPercentage: 0,
                };
            }
            return null;
        })
        .filter((t) => t != null);

    const getCalculations = (round: number) =>
        exerciseTypeSchema
            .map(
                ({exerciseType, topSetRepsTarget, numberOfSets, backoffPercentage}) => {
                    const reportedWeight = handler.maxes[exerciseType.uuid]?.weight;
                    const reportedReps = handler.maxes[exerciseType.uuid]?.reps;

                    if (reportedWeight == null || reportedReps == null) {
                        return null;
                    }

                    const weight = new Decimal(reportedWeight);
                    const oneRepMax = weight.times(
                        new Decimal(1).plus(new Decimal(reportedReps).dividedBy(30)),
                    );
                    const estimatedWeight = oneRepMax
                        .dividedBy(
                            new Decimal(1).plus(
                                new Decimal(topSetRepsTarget).dividedBy(30),
                            ),
                        )
                        .plus(
                            new Decimal(
                                handler.liveProfile.is_imperial ? "2.26796185" : "2.5",
                            ).times(round),
                        );

                    return {
                        exerciseType,
                        oneRepMax,
                        sets: [...Array(numberOfSets).keys()].map((index) => ({
                            weight: estimatedWeight
                                .minus(
                                    estimatedWeight
                                        .times(backoffPercentage * index)
                                        .dividedBy(100),
                                )
                                .dividedBy(
                                    handler.liveProfile.is_imperial ? "0.45359237" : 1,
                                ),
                            reps: topSetRepsTarget + index * 0,
                        })),
                    };
                },
            )
            .filter((c) => c != null);

    const fullSchedule = [...Array(12).keys()].map((weekNumber) => {
        const calculations = getCalculations(weekNumber);

        return [
            {
                title: "Workout A: Bench & Squat",
                name: "A: Bench & Squat",
                exerciseTypes: calculations.filter(
                    (c) =>
                        c.exerciseType.uuid == constants.EXERCISE_TYPES.BENCH ||
                        c.exerciseType.uuid == constants.EXERCISE_TYPES.SQUAT,
                ),
                accessories: ["Triceps – 2 x 10"],
            },
            {
                title: "Workout B: Press & Deadlift",
                name: "B: Press & Deadlift",
                exerciseTypes: calculations
                    .filter(
                        (c) => c.exerciseType.uuid == constants.EXERCISE_TYPES.PRESS,
                    )
                    .concat(
                        calculations.filter(
                            (c) =>
                                c.exerciseType.uuid ==
                                constants.EXERCISE_TYPES.DEADLIFT,
                        ),
                    ),
                accessories: ["Biceps – 2 x 10"],
            },
        ];
    });

    const flatSchedule = fullSchedule.flat();
    const chunkedSchedule = [];
    for (let i = 0; i < flatSchedule.length; i += 3) {
        chunkedSchedule.push(flatSchedule.slice(i, i + 3));
    }

    const weeks = chunkedSchedule.map((workouts, index) => {
        return {
            name: `Week ${index + 1}`,
            workouts: workouts,
        };
    });

    const schedule = fullSchedule[0];

    function generateScheduleContent(
        schedule: {
            title: string;
            exerciseTypes: {
                exerciseType: {name: string};
                oneRepMax: Decimal;
                sets: {
                    weight: Decimal;
                    reps: number;
                }[];
            }[];
            accessories: string[];
        }[],
    ): {headers: string[]; rows: string[][]} {
        // Headers are just the titles of the schedule
        const headers = schedule.map((day) => day.title);

        // Create arrays for each day's content (columns)
        const columns = schedule.map((day) => {
            const col: string[] = [];

            // Add exercise names and their sets
            day.exerciseTypes.forEach((exercise) => {
                col.push(exercise.exerciseType.name); // Exercise name
                exercise.sets.forEach((set) => {
                    col.push(`${getHumanWeight(set.weight).toString()}x${set.reps}`); // Set details
                });
            });

            // Add accessories after the exercises
            day.accessories.forEach((accessory) => {
                col.push(accessory); // Accessory details
            });

            return col;
        });

        // Determine the maximum number of rows
        const maxRows = Math.max(...columns.map((col) => col.length));

        // Pad shorter columns with empty cells and convert columns to rows
        const rows: string[][] = Array.from({length: maxRows}, (_, rowIndex) => {
            return schedule.map((_, dayIndex) => columns[dayIndex][rowIndex] || "");
        });

        return {headers, rows};
    }
    function formatTable(headers: string[], rows: string[][]): string {
        // Create the header row
        const headerRow = "|" + headers.join("|") + "|";

        // Create the separator row
        const separatorRow = "|" + headers.map(() => ":-").join("|") + "|";

        // Build the content rows
        const tableRows = rows.map((row) => "|" + row.join("|") + "|");

        // Combine all parts into the final string
        return [headerRow, separatorRow, ...tableRows].join("\n");
    }

    const {headers, rows} = generateScheduleContent(schedule);

    const formatEnum = (value: string) => {
        const replaced = value.replace("_", " ").toLowerCase();
        return replaced[0].toUpperCase() + replaced.slice(1);
    };
    // Then, format the table with separators and pipes
    const markdown = [];

    markdown.push("## Stats");
    markdown.push(
        formatTable(
            ["BMR", "TDEE", "Age", "Weight", "Height"],
            [
                [
                    handler.general.results.bmr,
                    handler.general.results.tdee,
                    handler.age?.toString() ?? "-",
                    utils.formatDecimalAndUnit(
                        handler.liveProfile.weight != null
                            ? new Decimal(handler.liveProfile.weight)
                                  .dividedBy(
                                      handler.liveProfile.is_imperial
                                          ? "0.45359237"
                                          : 1,
                                  )
                                  .toDecimalPlaces(1)
                            : null,
                        "weight",
                        handler.liveProfile.is_imperial,
                    ),
                    utils.formatDecimalAndUnit(
                        handler.liveProfile.height != null
                            ? new Decimal(handler.liveProfile.height)
                                  .dividedBy(
                                      handler.liveProfile.is_imperial ? "2.54" : 1,
                                  )
                                  .toDecimalPlaces(1)
                            : null,
                        "length",
                        handler.liveProfile.is_imperial,
                    ),
                ],
            ],
        ),
    );
    markdown.push("## Settings");
    markdown.push(
        formatTable(
            ["Goal", "Activity", "Nutrition"],
            [
                [
                    handler.settingsForm.values.goal,
                    handler.settingsForm.values.activity,
                    nutritionForm.value,
                ].map(formatEnum),
            ],
        ),
    );
    markdown.push("## Macros");
    markdown.push(
        formatTable(
            ["Day", "Calories", "Protein", "Fat", "Carbs"],
            handler.general.results.targets.map((target) => [
                target.name,
                target.calories_nearest,
                target.protein_nearest,
                target.fat_nearest,
                target.carbs_nearest,
            ]),
        ),
    );
    markdown.push("## Maxes");
    markdown.push(
        formatTable(
            ["Exercise", "Weight", "Reps"],
            exerciseTypeSchema.map(({exerciseType}) => {
                const max = handler.maxes[exerciseType.uuid];
                if (max && max.weight != null && max.reps != null) {
                    const formatted = new Decimal(max.weight)
                        .dividedBy(handler.liveProfile.is_imperial ? "0.45359237" : 1)
                        .toString();
                    return [
                        exerciseType.name,
                        `${formatted} ${handler.liveProfile.is_imperial ? "lb" : "kg"}`,
                        max.reps.toString(),
                    ];
                }
                return [exerciseType.name, "-", "-"];
            }),
        ),
    );

    markdown.push("## Workouts");
    markdown.push(formatTable(headers, rows));

    if (router.route == "review") {
        const snippet = markdown.join("\n");
        const content = (
            <div className="grid grid-cols-1 gap-4 *:rounded-md *:border *:p-4 sm:grid-cols-2">
                <div>
                    {
                        getStep(
                            {route: "profile", push: () => {}},
                            nutritionForm,
                            workouts,
                            handler,
                        ).content
                    }
                </div>
                <div>
                    {
                        getStep(
                            {route: "goal", push: () => {}},
                            nutritionForm,
                            workouts,
                            handler,
                        ).content
                    }
                </div>
                <div>
                    {
                        getStep(
                            {route: "activity", push: () => {}},
                            nutritionForm,
                            workouts,
                            handler,
                        ).content
                    }
                </div>
                <div>
                    {
                        getStep(
                            {route: "nutrition", push: () => {}},
                            nutritionForm,
                            workouts,
                            handler,
                        ).content
                    }
                </div>
                <div>
                    {
                        getStep(
                            {route: "settings", push: () => {}},
                            nutritionForm,
                            workouts,
                            handler,
                        ).content
                    }
                </div>
                <div>
                    {
                        getStep(
                            {route: "maxes", push: () => {}},
                            nutritionForm,
                            workouts,
                            handler,
                            true,
                        ).content
                    }
                </div>
                <div>
                    {
                        getStep(
                            {route: "results", push: () => {}},
                            nutritionForm,
                            workouts,
                            handler,
                        ).content
                    }
                </div>
                <div>
                    {
                        getStep(
                            {route: "program", push: () => {}},
                            nutritionForm,
                            workouts,
                            handler,
                        ).content
                    }
                </div>
                <div className="space-y-2 sm:col-span-2" id="reddit">
                    <div className="mb-4 space-y-2 leading-4">
                        <h2 className="text-xl">Reddit Post</h2>
                        <p>
                            Copy the formatted text below and paste it into a Reddit
                            post to get feedback.
                        </p>
                        <p>
                            Be sure to select <strong>Markdown</strong> when editing the
                            post.
                        </p>
                    </div>
                    <textarea
                        className="min-h-[550px] w-full border-none p-0 text-[13px]/4 text-gray-700"
                        onChange={() => {}}
                        value={snippet}
                    />
                </div>
            </div>
        );

        return {
            content,
            back: {
                title: <>Start Over</>,
                onClick: () => {
                    router.push("profile");
                    void analytics.track("RESTARTED_CALCULATOR_JOURNEY", {
                        diet: "GENERAL",
                    });
                },
            },
            next: {
                title: <>Save</>,
                onClick: async () => {},
            },
        };
    }

    switch (router.route) {
        case "intro":
            return {
                content: (
                    <div className="flex h-full min-h-[500px] flex-col justify-center gap-8 text-center *:block *:space-y-8">
                        <div className="text-3xl text-pretty">
                            Welcome. <br />
                            Let’s get started.
                        </div>

                        <div className="text-xl/4 text-pretty">
                            <div>Answer a few questions.</div>
                            <div>
                                We’ll give you a{" "}
                                <span className="text-indigo-700">diet plan</span>
                            </div>
                            <div>
                                A <span className="text-indigo-700">workout</span>{" "}
                                program
                            </div>
                            <div>
                                And recommended{" "}
                                <span className="text-indigo-700">habits</span>
                            </div>
                        </div>
                    </div>
                ),
                back: null,
                next: {
                    title: "Start",
                    route: "profile",
                },
            };
        case "profile":
            return {
                content: <onboarding.ProfileForm {...handler} />,
                back: null,
                next: {
                    disabled: handler.validatedProfile == null,
                    route: "goal",
                },
            };
        case "goal":
            return {
                content: (
                    <div className="space-y-4 *:block *:space-y-3">
                        <forms.Field
                            field={handler.settingsForm.fields.goal}
                            component={onboarding.RadioSelect}
                            group
                            label={<h2 className="text-xl">What is your goal?</h2>}
                            help={<div>Helps us calculate your caloric needs.</div>}
                            props={{
                                descriptions: {
                                    WEIGHT_LOSS:
                                        "Lose at most a pound a week. Mostly from fat. Sustainable long term.",
                                    AGGRESSIVE_WEIGHT_LOSS: false,
                                    HEALTH: "Get healthier and slowly replace fat with muscle.",
                                    BULK: "Put on muscle with minimal fat. Slow and steady.",
                                    MASS: false,
                                },
                            }}
                        />
                    </div>
                ),
                back: {
                    route: "profile",
                },
                next: {
                    route: "activity",
                },
            };
        case "activity":
            return {
                content: <onboarding.ActivityForm {...handler} />,
                back: {
                    route: "goal",
                },
                next: {
                    route: "nutrition",
                },
            };
        case "nutrition":
            return {
                content: (
                    <div className="space-y-4 *:block *:space-y-3">
                        <forms.Field
                            field={nutritionForm}
                            component={onboarding.RadioSelect}
                            group
                            label={<h2 className="text-xl">Nutrition and macros</h2>}
                            help={<div>Pick a breakdown of macros.</div>}
                            props={{
                                descriptions: {
                                    STANDARD:
                                        "High protein always. The rest is balanced between fat and carbs.",
                                    PROTEIN:
                                        "Extra high on protein. Helps with satiety. Great for weight loss.",
                                    CUSTOM: "Tweak all these settings yourself to come up with custom macros.",
                                },
                            }}
                        />
                    </div>
                ),
                back: {
                    route: "activity",
                },
                next: {
                    route: nutritionForm.value == "CUSTOM" ? "settings" : "results",
                },
            };
        case "settings": {
            const getPercentages = (value: string | null) => {
                if (value == null) {
                    return {
                        fat: 0,
                        carbs: 0,
                    };
                }
                const fat = Math.round(parseFloat(value) * 100);
                return {
                    fat,
                    carbs: 100 - fat,
                };
            };

            const nutrients = getPercentages(handler.general.form.values.fat_carbs);

            return {
                content: (
                    <div className="space-y-6">
                        <div className="flex flex-col gap-4 *:space-y-2">
                            <h2 className="text-xl">Nutrition settings</h2>
                            <label>
                                <div>Grams of protein</div>
                                <forms.Field
                                    field={
                                        handler.general.form.fields.protein_multiplier
                                    }
                                    noLabel
                                />
                            </label>
                            <label>
                                <div>Surplus or deficit</div>
                                <forms.Field
                                    field={handler.general.form.fields.variance}
                                    noLabel
                                    suffix="%"
                                />
                            </label>
                            <label>
                                <div>Nutrient split</div>
                                <div className="flex items-center gap-1">
                                    <div className="min-w-24 rounded-md bg-sky-100 px-1 py-0.5 text-center text-xs sm:px-2">
                                        Fat {nutrients.fat} %
                                    </div>
                                    <div className="flex-1">
                                        <forms.Field
                                            field={
                                                handler.general.form.fields.fat_carbs
                                            }
                                            noLabel
                                        />
                                    </div>
                                    <div className="min-w-24 rounded-md bg-sky-100 px-1 py-0.5 text-center text-xs sm:px-2">
                                        Carbs {nutrients.carbs} %
                                    </div>
                                </div>
                            </label>
                        </div>
                    </div>
                ),
                back: {
                    route: "nutrition",
                },
                next: {
                    onClick: () => {
                        if (forms.ready(handler.general.form)) {
                            router.push("results");
                        }
                    },
                },
            };
        }
        case "results":
            return {
                content: (
                    <div className="flex h-full min-h-[520px] flex-col justify-center gap-8 text-center *:block *:space-y-8">
                        <div className="text-3xl text-pretty">
                            Eat{" "}
                            <span className="text-indigo-700">
                                {handler.general.results.targets[0].calories_nearest}
                            </span>{" "}
                            calories per day
                        </div>
                        <div className="text-xl/8 text-pretty">
                            <div>
                                Get{" "}
                                <span className="text-indigo-700">
                                    {handler.general.results.targets[0].protein_nearest}
                                </span>{" "}
                                grams of{" "}
                                <span className="text-indigo-700">protein</span>
                            </div>
                            <div>
                                Aim for{" "}
                                <span className="text-indigo-700">
                                    {handler.general.results.targets[0].fat_nearest}
                                </span>{" "}
                                grams of <span className="text-indigo-700">fat</span>
                            </div>
                            <div>
                                And{" "}
                                <span className="text-indigo-700">
                                    {handler.general.results.targets[0].carbs_nearest}
                                </span>{" "}
                                grams of <span className="text-indigo-700">carbs</span>
                            </div>
                            <div>
                                Eat <span className="text-indigo-700">whole foods</span>{" "}
                                and <span className="text-indigo-700">avoid</span>{" "}
                                processed foods.
                            </div>
                        </div>
                    </div>
                ),
                back: {
                    route: nutritionForm.value == "CUSTOM" ? "settings" : "nutrition",
                },
                next: {
                    title: "Workouts",
                    route: "maxes",
                },
            };
        case "maxes":
            return {
                content: (
                    <div className="@container space-y-2">
                        <div className="px-4 py-2">
                            <div className="mb-2 flex gap-2">
                                <h2 className="mr-auto text-xl">Exercise Maxes</h2>
                                {isEmbedded != true && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            handler.setMaxes({
                                                [constants.EXERCISE_TYPES.DEADLIFT]: {
                                                    weight: "20.41165665",
                                                    reps: 5,
                                                },
                                                [constants.EXERCISE_TYPES.SQUAT]: {
                                                    weight: "20.41165665",
                                                    reps: 5,
                                                },
                                                [constants.EXERCISE_TYPES.PRESS]: {
                                                    weight: "20.41165665",
                                                    reps: 5,
                                                },
                                                [constants.EXERCISE_TYPES.BENCH]: {
                                                    weight: "20.41165665",
                                                    reps: 5,
                                                },
                                            });
                                            router.push("program");
                                        }}
                                        className="rounded-md bg-white px-2 py-1 text-xs font-semibold text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset hover:bg-gray-50"
                                    >
                                        I Don’t Know
                                    </button>
                                )}
                            </div>
                            <p>
                                Pick a weight and enter how many reps you can do. Max
                                effort but <strong>not to failure</strong>.
                            </p>
                        </div>
                        <div className="grid grid-cols-1 gap-3 @lg:grid-cols-2">
                            {exerciseTypeSchema.map(
                                ({exerciseType, topSetRepsTarget}) => (
                                    <div
                                        key={exerciseType.uuid}
                                        className="rounded-lg bg-indigo-100 px-4 py-3"
                                    >
                                        <h2 className="text-base">
                                            {exerciseType.name}
                                        </h2>
                                        <div className="mt-1 flex items-center gap-2">
                                            <widgets.WeightInput
                                                isImperial={
                                                    handler.liveProfile.is_imperial
                                                }
                                                name={`${exerciseType.uuid}-weight`}
                                                value={
                                                    handler.maxes[exerciseType.uuid]
                                                        ?.weight ?? null
                                                }
                                                onChange={(value) => {
                                                    handler.setMaxes(
                                                        produce(
                                                            handler.maxes,
                                                            (draft) => {
                                                                const {uuid} =
                                                                    exerciseType;
                                                                if (
                                                                    draft[uuid] == null
                                                                ) {
                                                                    draft[uuid] = {
                                                                        weight: null,
                                                                        reps: null,
                                                                    };
                                                                }
                                                                draft[uuid].weight =
                                                                    value;
                                                            },
                                                        ),
                                                    );
                                                }}
                                            />
                                            <typography.Icon icon="custom-bolder-cross" />
                                            <widgets.Suffixed suffix="reps">
                                                <widgets.IntegerInput
                                                    name={`${exerciseType.uuid}-reps`}
                                                    placeholder={topSetRepsTarget.toString()}
                                                    value={
                                                        handler.maxes[exerciseType.uuid]
                                                            ?.reps ?? null
                                                    }
                                                    onChange={(value) => {
                                                        handler.setMaxes(
                                                            produce(
                                                                handler.maxes,
                                                                (draft) => {
                                                                    const {uuid} =
                                                                        exerciseType;
                                                                    if (
                                                                        draft[uuid] ==
                                                                        null
                                                                    ) {
                                                                        draft[uuid] = {
                                                                            weight: null,
                                                                            reps: null,
                                                                        };
                                                                    }
                                                                    draft[uuid].reps =
                                                                        value;
                                                                },
                                                            ),
                                                        );
                                                    }}
                                                />
                                            </widgets.Suffixed>
                                        </div>
                                    </div>
                                ),
                            )}
                        </div>
                    </div>
                ),
                back: {
                    route: "results",
                },
                next: {
                    disabled:
                        Object.values(handler.maxes).filter(
                            (w) => w?.reps != null && w.weight != null,
                        ).length < 4,
                    route: "program",
                },
            };
        case "program":
            return {
                content: (
                    <div className="space-y-2">
                        {registrationDialog.dialog}
                        <div className="mb-4 space-y-2 px-4">
                            <h2 className="text-xl">Programming</h2>
                            <p>
                                You’ll work out <strong>three</strong> times a week.
                            </p>
                            <p>
                                First week is <strong>Workout A</strong>, then{" "}
                                <strong>Workout B</strong>, and finally{" "}
                                <strong>Workout A</strong> again.
                            </p>
                            <p>
                                Second week is <strong>Workout B</strong>, then{" "}
                                <strong>Workout A</strong>, and finally{" "}
                                <strong>Workout B</strong>.
                            </p>
                            <p>Don’t worry, we map this out for you in the app.</p>
                        </div>
                        {schedule.map((day) => (
                            <div
                                key={day.title}
                                className="space-y-1 rounded-lg bg-indigo-100 px-4 py-2"
                            >
                                <h2 className="text-xl">{day.title}</h2>
                                {day.exerciseTypes.map((exerciseType) => (
                                    <div
                                        key={exerciseType.exerciseType.uuid}
                                        className="ml-4"
                                    >
                                        <h2 className="text-base">
                                            {exerciseType.exerciseType.name}
                                        </h2>
                                        <ul className="list-inside">
                                            {exerciseType.sets.map((set, index) => (
                                                <li
                                                    key={index}
                                                    className="ml-1 list-disc"
                                                >
                                                    {getHumanWeight(
                                                        set.weight,
                                                    ).toString()}{" "}
                                                    {handler.liveProfile.is_imperial
                                                        ? "lb"
                                                        : "kg"}{" "}
                                                    x {set.reps}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                                {day.accessories.map((accessory) => (
                                    <div key={accessory} className="ml-4">
                                        <h2 className="text-base">{accessory}</h2>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                ),
                back: {
                    route: "maxes",
                },
                next: {
                    title: "Start Tracking",
                    onClick: async () => {
                        const registrationResult = await registrationDialog();

                        if (registrationResult == null) {
                            return;
                        }

                        void analytics.registered(
                            registrationResult.user.tracking_key,
                            "start",
                            "trial",
                        );

                        const program: server.journal.workouts.Program.input = {
                            uuid: crypto.randomUUID(),
                            creation: {
                                mode: "PROGRAM",
                                type: "CUSTOM",
                                data: null,
                            },
                            template: {
                                visibility: "PRIVATE",
                                description: "",
                                name: "Barbell Linear Progression",
                            },
                            exercise_maxes: Object.entries(handler.maxes)
                                .filter(
                                    ([, max]) =>
                                        max?.reps != null && max.weight != null,
                                )
                                .map(([uuid, max]) => ({
                                    type: "AMRAP",
                                    exercise_type: {uuid: uuid as UUID},
                                    reps: max?.reps ?? 0,
                                    weight: max?.weight ?? "0",
                                })),
                            workout_groups: weeks.map((workoutGroup) => ({
                                ...workoutGroup,
                                description: "",
                                uuid: crypto.randomUUID(),
                                workouts: workoutGroup.workouts.map((workout) => ({
                                    uuid: crypto.randomUUID(),
                                    maxes: "EXISTING_MAXES",
                                    mode: "NORMAL",
                                    name: workout.name,
                                    instructions: "",
                                    questions: [],
                                    sections: [
                                        {
                                            uuid: crypto.randomUUID(),
                                            name: null,
                                            copied_from: null,
                                            instructions: "",
                                            type: "BASIC",
                                            duration: null,
                                            rounds: null,
                                            groups: workout.exerciseTypes.map(
                                                (exerciseType) => ({
                                                    uuid: crypto.randomUUID(),
                                                    exercises: [
                                                        {
                                                            uuid: crypto.randomUUID(),
                                                            type: {
                                                                uuid: exerciseType
                                                                    .exerciseType.uuid,
                                                            },
                                                            mode: "NORMAL",
                                                            structure: "STRAIGHT",
                                                            instructions: "",
                                                            video_uid: null,
                                                            progression: null,
                                                            rest_duration: null,
                                                            sets: exerciseType.sets.map(
                                                                (set) => ({
                                                                    type: "WORKING",
                                                                    progression: null,
                                                                    side: null,
                                                                    drop_of: null,
                                                                    uuid: crypto.randomUUID(),
                                                                    reps: set.reps,
                                                                    weight: set.weight
                                                                        .times(
                                                                            handler
                                                                                .liveProfile
                                                                                .is_imperial
                                                                                ? "0.45359237"
                                                                                : 1,
                                                                        )
                                                                        .toString(),
                                                                    duration_type: null,
                                                                    status: "INCOMPLETE",
                                                                    rpe: null,
                                                                    instructions: "",
                                                                    weight_type:
                                                                        "VALUE",
                                                                    weight_percentage:
                                                                        null,
                                                                    distance_type: null,
                                                                    speed_type: null,
                                                                    duration: null,
                                                                    distance: null,
                                                                    speed: null,
                                                                    incline: null,
                                                                    resistance: null,
                                                                    attributes: [],
                                                                }),
                                                            ),
                                                        },
                                                    ],
                                                }),
                                            ),
                                        },
                                    ],
                                })),
                            })),
                            program_users: null,
                        };

                        const targets = handler.general.results.targets.map(
                            (target) => ({
                                uuid: crypto.randomUUID(),
                                name: target.name,
                                fiber_target: null,
                                calories_target: target.calories_nearest,
                                protein_mode: "AT_LEAST" as const,
                                protein_target: target.protein_nearest,
                                fat_target: target.fat_nearest,
                                carbs_target: target.carbs_nearest,
                                net_carbs_target: null,
                            }),
                        );

                        const today = dates.formatISO(new Date(), {
                            representation: "date",
                        });

                        await server.journal.day.start({
                            program,
                            measurements: {
                                day: {
                                    key: today,
                                    user_id: registrationResult.user.id,
                                },
                                weight: handler.liveProfile.weight,
                                waist: null,
                                navel: null,
                                body_fat: null,
                                hips: null,
                                neck: null,
                                thigh: null,
                                bicep: null,
                                chest: null,
                            },
                            planner: {
                                user_id: registrationResult.user.id,
                                assignments: handler.general.results.assignments.reduce<
                                    Record<number, UUID>
                                >((acc, assignment, index) => {
                                    acc[index] = targets[assignment.reference].uuid;
                                    return acc;
                                }, {}),
                                targets,
                                profile: {...handler.liveProfile, name: ""},
                                habits: {
                                    STEPS: 10000,
                                },
                            },
                        });

                        window.location.href = reverse("journal");
                    },
                },
            };
        default: {
            const _exhaustiveCheck: never = router.route;
            return _exhaustiveCheck;
        }
    }
}

function useNutritionForm() {
    const [nutrition, setNutrition] = React.useState<Nutrition>("STANDARD");

    return forms.useField(
        {
            kind: "select",
            nullable: false,
            options: [
                ["STANDARD", "Standard"],
                ["PROTEIN", "High Protein"],
                ["CUSTOM", "Custom"],
            ],
            required: true,
        },
        {value: nutrition, onChange: setNutrition},
    );
}

export function Start({workouts}: server.journal.templates.Start) {
    const {profile} = useSiteContext();
    const props = onboarding.useHooks({diet: "GENERAL", requireName: false, profile});

    React.useEffect(() => {
        if (props.settingsForm.values.goal == "WEIGHT_LOSS") {
            props.general.onPresetChange(0);
        } else if (props.settingsForm.values.goal == "BULK") {
            props.general.onPresetChange(1);
        } else {
            props.general.onPresetChange(2);
        }
    }, [props.settingsForm.values.goal]);

    const [route, setRoute] = React.useState<Route>("intro");

    const nutritionForm = useNutritionForm();

    React.useEffect(() => {
        if (nutritionForm.value == "STANDARD") {
            props.general.form.update((prev) => ({
                ...prev,
                protein_multiplier: "1.5",
                fat_carbs: general.presets[0].fat_carbs,
            }));
        } else if (nutritionForm.value == "PROTEIN") {
            props.general.form.update((prev) => ({
                ...prev,
                protein_multiplier: "2.5",
                fat_carbs: general.presets[0].fat_carbs,
            }));
        }
    }, [nutritionForm.value]);

    const {content, back, next} = getStep(
        {route, push: setRoute},
        nutritionForm,
        workouts,
        props,
    );

    const bottomSection = (
        <div className="flex flex-row-reverse justify-between bg-gray-50 px-4 py-3">
            <button
                type="button"
                disabled={next.disabled}
                onClick={() => {
                    if (next.route != null) {
                        void analytics.track("CLICKED_CALCULATOR_JOURNEY_NEXT", {
                            route_name: next.route,
                            diet: "GENERAL",
                        });
                        setRoute(next.route);
                    } else {
                        next.onClick();
                    }
                }}
                className="rounded-md bg-indigo-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 focus-visible:outline-solid disabled:opacity-50"
            >
                {next.title == undefined || typeof next.title == "string" ? (
                    <typography.Icon
                        icon="custom-chevron-right"
                        large="medium"
                        align="right"
                    >
                        {next.title ?? "Next"}
                    </typography.Icon>
                ) : (
                    next.title
                )}
            </button>

            {back != null && (
                <button
                    type="button"
                    className="rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset hover:bg-gray-50"
                    onClick={() => {
                        if (back.route != null) {
                            void analytics.track("CLICKED_CALCULATOR_JOURNEY_BACK", {
                                route_name: back.route,
                                diet: "GENERAL",
                            });
                            setRoute(back.route);
                        } else {
                            back.onClick();
                        }
                    }}
                >
                    {back.title == undefined || typeof back.title == "string" ? (
                        <typography.Icon
                            icon="custom-chevron-left"
                            large="medium"
                            align="left"
                        >
                            {back.title ?? "Back"}
                        </typography.Icon>
                    ) : (
                        back.title
                    )}
                </button>
            )}
        </div>
    );

    return (
        <ImperialCaloriesContext.Provider
            value={{isImperial: props.liveProfile.is_imperial, setIsImperial: () => {}}}
        >
            <div className={appStyles.app}>
                <div
                    className={appStyles.navigationAndManager}
                    style={{borderBottom: "1px solid #CCC", marginBottom: 10}}
                >
                    <div className="mx-auto flex w-full max-w-[1000px] items-center gap-1 px-4 py-2">
                        <div className="flex flex-1 flex-col items-center justify-center gap-0">
                            <graphics.Logo className="-mt-3 size-16" />
                            <div className="-mt-4 font-medium text-pretty text-indigo-800">
                                Nutrition - Workouts - Habits
                            </div>
                        </div>
                    </div>
                </div>

                <Scene
                    actions={[]}
                    tabs={[]}
                    topSection={null}
                    bottomSection={bottomSection}
                    more={null}
                    locator={null}
                >
                    {content}
                </Scene>
            </div>
        </ImperialCaloriesContext.Provider>
    );
}

export function Template(props: server.journal.templates.Start) {
    const STATIC_URL = useStaticURL();
    analytics.useTrackPageView();

    return (
        <Shell>
            <Static />
            <meta charSet="UTF-8" />
            <title>Get Started | Joy Health Tracker</title>
            <meta
                name="viewport"
                content="viewport-fit=cover, width=device-width, initial-scale=1.0, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no"
            />
            <link rel="icon" href={`${STATIC_URL}favicon.png`} type="image/png" />
            <Start {...props} />
        </Shell>
    );
}
